/**
 * In-memory, per-process rate limiter for anonymous email-sending server
 * actions (magic-link sign-in, job-alert confirmation) — these accept a
 * free-text email address with no auth and no CAPTCHA, so with no throttle a
 * script can send unlimited mail to any address it likes: it costs the sender
 * nothing, burns Resend/GoTrue send quota, and looks to the recipient like
 * this app is spamming them.
 *
 * In-memory only works because the app now runs as one long-lived
 * self-hosted process behind Caddy, not fanned out across many short-lived
 * serverless invocations (each of which would have its own empty Map) — this
 * would have been close to useless on the old Vercel functions setup.
 * A multi-replica deploy would need a shared store (DB/Redis) instead; note
 * that limitation if this ever scales past one instance.
 */
const hits = new Map<string, number[]>();

/** Sweep old entries occasionally so the Map doesn't grow forever. */
let lastSweep = 0;
function sweep(now: number, windowMs: number): void {
  if (now - lastSweep < 60_000) return;
  lastSweep = now;
  for (const [key, timestamps] of hits) {
    const kept = timestamps.filter((t) => now - t < windowMs);
    if (kept.length === 0) hits.delete(key);
    else hits.set(key, kept);
  }
}

/**
 * Returns true if `key` is still within its allowance (and records this hit),
 * false if it should be rejected. `key` should combine the action name with
 * the thing being protected (e.g. `magic-link:${email}`) so different actions
 * never share a bucket.
 */
export function allowRequest(key: string, max: number, windowMs: number): boolean {
  const now = Date.now();
  sweep(now, windowMs);
  const timestamps = (hits.get(key) ?? []).filter((t) => now - t < windowMs);
  if (timestamps.length >= max) {
    hits.set(key, timestamps);
    return false;
  }
  timestamps.push(now);
  hits.set(key, timestamps);
  return true;
}

/**
 * The caller's real IP, for the throttles that must survive an attacker
 * rotating the thing they control.
 *
 * Keying a limiter on the submitted EMAIL is necessary but not sufficient: the
 * abuse case for an anonymous email-sending action is sending to MANY different
 * addresses, which an email-keyed bucket never sees twice. The IP is the part
 * the sender cannot vary for free.
 *
 * Prefer `cf-connecting-ip`. Everything here sits behind Cloudflare, which sets
 * that header itself and strips any client-supplied copy, so it is the one
 * value in the request an attacker cannot forge. `x-forwarded-for` cannot be
 * used naively in either direction: the FIRST entry is whatever the client sent
 * (trivially spoofed, so a spoofing attacker gets unlimited fresh buckets), and
 * the LAST entry is Cloudflare's own edge IP (identical for every visitor, so
 * one abuser would rate-limit the entire internet). The real client is the
 * second-to-last entry, which is what the fallback takes.
 */
export function clientIp(headers: Headers): string {
  const cf = headers.get("cf-connecting-ip")?.trim();
  if (cf) return cf;
  const chain =
    headers
      .get("x-forwarded-for")
      ?.split(",")
      .map((p) => p.trim())
      .filter(Boolean) ?? [];
  if (chain.length >= 2) return chain[chain.length - 2]!;
  if (chain.length === 1) return chain[0]!;
  return headers.get("x-real-ip")?.trim() || "unknown";
}

/**
 * A hidden form field real people never fill in. One input, no third-party
 * CAPTCHA, no consent problem, no accessibility cost (aria-hidden + tabIndex
 * -1) — and it stops the naive bots outright, which is most of them.
 */
export const HONEYPOT_FIELD = "company_website";

/** True when the honeypot was filled — treat the submission as a bot. */
export function honeypotTripped(formData: FormData): boolean {
  return String(formData.get(HONEYPOT_FIELD) ?? "").trim().length > 0;
}

/**
 * Per-IP allowance for anonymous actions that send email to a caller-supplied
 * address. Deliberately generous for a person (a household or office behind
 * one NAT can legitimately set up a few alerts) and useless for a relay.
 */
export const EMAIL_SEND_IP_MAX = 8;
export const EMAIL_SEND_IP_WINDOW_MS = 60 * 60 * 1000;

/** Self-check: `npx tsx src/lib/rate-limit.ts` */
if (process.argv[1] && process.argv[1].endsWith("rate-limit.ts")) {
  const assert = (cond: boolean, msg: string) => {
    if (!cond) {
      console.error(`FAIL ${msg}`);
      process.exitCode = 1;
    }
  };
  const h = (o: Record<string, string>) => new Headers(o);
  assert(
    clientIp(h({ "cf-connecting-ip": "1.2.3.4", "x-forwarded-for": "9.9.9.9, 1.2.3.4, 172.71.1.1" })) ===
      "1.2.3.4",
    "cf-connecting-ip wins",
  );
  // The spoof case: an attacker prepends a fake entry; we must not read it.
  assert(
    clientIp(h({ "x-forwarded-for": "6.6.6.6, 1.2.3.4, 172.71.1.1" })) === "1.2.3.4",
    "second-to-last, not first",
  );
  // The measured case (2026-09-24, chief.concept.zone): Caddy replaced the
  // chain with a Cloudflare edge node, and they rotate. Two requests from one
  // caller must key the same, or the budget is a budget of one.
  assert(
    clientIp(h({ "cf-connecting-ip": "2a02:5d7::1", "x-forwarded-for": "162.158.102.229" })) ===
      clientIp(h({ "cf-connecting-ip": "2a02:5d7::1", "x-forwarded-for": "172.68.159.172" })),
    "one caller behind two edge nodes shares one bucket",
  );
  assert(clientIp(h({ "x-forwarded-for": "1.2.3.4" })) === "1.2.3.4", "single entry");
  assert(clientIp(h({ "x-real-ip": "5.5.5.5" })) === "5.5.5.5", "x-real-ip fallback");
  assert(clientIp(h({})) === "unknown", "no headers");

  const fd = new FormData();
  assert(!honeypotTripped(fd), "empty honeypot passes");
  fd.set(HONEYPOT_FIELD, "http://spam.example");
  assert(honeypotTripped(fd), "filled honeypot trips");

  // The limiter itself: allow up to max, then refuse, and keep refusing.
  const key = `selfcheck:${Math.random()}`;
  let allowed = 0;
  for (let i = 0; i < 5; i++) if (allowRequest(key, 3, 60_000)) allowed++;
  assert(allowed === 3, `expected 3 allowed, got ${allowed}`);
  assert(!allowRequest(key, 3, 60_000), "stays refused");

  if (!process.exitCode) console.log("rate-limit OK — 9 cases");
}
